/**
 * DisconnectedOverlay - Full-screen overlay shown when WebSocket connection fails
 *
 * Displays when auto-reconnect attempts are exhausted, allowing the user to
 * manually retry the connection without needing to refresh the page.
 */

import { useState, useCallback } from 'react';
import { WifiOff, RefreshCw, ArrowLeft } from 'lucide-react';
import { AnkhSpinner } from '@/components/common/AnkhSpinner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
interface DisconnectedOverlayProps {
  onRetrySuccess?: () => void;
  /** Reconnect function to call when user clicks retry */
  onReconnect: () => Promise<void>;
  /** Optional: navigate back to lobby */
  onReturnToLobby?: () => void;
}

export function DisconnectedOverlay({
  onRetrySuccess,
  onReconnect,
  onReturnToLobby,
}: DisconnectedOverlayProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    setRetryError(null);

    try {
      await onReconnect();
      onRetrySuccess?.();
    } catch {
      setRetryError('Connection failed. Please check your internet and try again.');
    } finally {
      setIsRetrying(false);
    }
  }, [onRetrySuccess, onReconnect]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-6 max-w-md px-6"
      >
        {/* Disconnected Icon */}
        <motion.div
          className="mx-auto w-24 h-24 rounded-full bg-destructive/10 flex items-center justify-center"
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <WifiOff className="h-12 w-12 text-destructive" />
        </motion.div>

        {/* Message */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-foreground">Connection Lost</h2>
          <p className="text-muted-foreground">
            The connection to the server was lost. This may happen due to network issues or
            inactivity.
          </p>
        </div>

        {/* Error Message */}
        {retryError && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-destructive"
          >
            {retryError}
          </motion.p>
        )}

        {/* Retry Button */}
        <div className="space-y-3">
          <Button onClick={handleRetry} disabled={isRetrying} size="lg" className="w-full max-w-xs">
            {isRetrying ? (
              <>
                <AnkhSpinner size="sm" className="mr-2" />
                Reconnecting...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-5 w-5" />
                Reconnect
              </>
            )}
          </Button>

          {onReturnToLobby && (
            <Button
              onClick={onReturnToLobby}
              variant="ghost"
              size="lg"
              className="w-full max-w-xs"
              disabled={isRetrying}
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Return to Lobby
            </Button>
          )}

          <p className="text-xs text-muted-foreground">
            If the problem persists, try refreshing the page.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
